import React, { useMemo } from "react";
import { Form } from "react-final-form";
import { Box, Button } from "@mui/material";

import { LoadingButton } from "ui";

import { DropzoneField } from "fond/form/fields";
import RequiredFiles from "fond/form/fields/RequiredFiles/RequiredFiles";
import { ProjectSubtype } from "fond/types";
import { getFilesFromEvent } from "fond/utils/files";
import { requireAll } from "fond/utils/validation";
import { Modal } from "fond/widgets";
import { validFileName } from "fond/widgets/Dropzone/utils";

import { getNBNFiles } from "./files";

interface UploadS18ModalProps {
  type: ProjectSubtype;
  onClose: () => void;
  onSubmit: (values: UploadFormData) => void;
}

export interface UploadFormData {
  Files: File[];
}

const UploadS18Modal: React.FC<UploadS18ModalProps> = ({ type, onClose, onSubmit }: UploadS18ModalProps) => {
  // The list of required files for the selected project type
  const s18Files = useMemo(() => getNBNFiles(type), [type]);

  return (
    <Modal
      open
      variant="primary"
      header="Upload input data"
      content={
        <Box mt={2} height="50%">
          <Form<UploadFormData>
            validateOnBlur
            onSubmit={onSubmit}
            subscription={{ submitting: true, valid: true, dirty: true }}
            render={({ handleSubmit, submitting, valid, dirty }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Box my={2} display="flex" columnGap={6}>
                    <DropzoneField
                      name="Files"
                      acceptedFiles={{
                        "application/json": [".dat", ".id", ".map", ".tab"],
                        "application/zip": [".zip"],
                      }}
                      fullWidth
                      message="Upload ZIP or individual files by using drag and drop, or by clicking to browse."
                      dropzoneValidator={validFileName(s18Files.map(({ file }) => file))}
                      validate={requireAll(s18Files.length)}
                      getFilesFromEvent={getFilesFromEvent}
                    />
                  </Box>
                  <RequiredFiles fieldName="Files" fileMap={s18Files} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                      py: 3,
                    }}
                  >
                    <Button type="button" data-testid="report-cancel-button" color="primary" onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      data-testid="report-save-button"
                      color="primary"
                      loading={submitting}
                      disabled={!dirty || !valid || submitting}
                    >
                      Upload
                    </LoadingButton>
                  </Box>
                </form>
              );
            }}
          />
        </Box>
      }
      onClose={onClose}
      DialogActionsProps={{ sx: { display: "none" } }}
    />
  );
};

export default UploadS18Modal;
